import { Map, fromJS } from 'immutable'
import { createReducer } from 'reducers/utils'
import { toMap } from 'utils/functional'
import {
  CREATE_COLLECTION_FAILURE,
  CREATE_COLLECTION_SUCCESS,
  CLEAR_CREATE_COLLECTION_NOTIFICATION,
  CLEAR_CREATED_COLLECTION,
  GET_COLLECTIONS_SUCCESS,
  GET_COLLECTIONS_REQUEST,
  GET_COLLECTIONS_FAILURE,
  GET_COLLECTION_SUCCESS,
  GET_COLLECTION_REQUEST,
  GET_COLLECTION_FAILURE,
  RESET_COLLECTIONS,
  RESET_COLLECTION,
  UPDATE_COLLECTION_SUCCESS,
  UPDATE_COLLECTION_FAILURE,
  UPDATE_COLLECTION_REQUEST,
  ACTIVATE_COLLECTION_REQUEST,
  ACTIVATE_COLLECTION_SUCCESS,
  ACTIVATE_COLLECTION_FAILURE,
  OBSOLETE_COLLECTION_REQUEST,
  OBSOLETE_COLLECTION_SUCCESS,
  OBSOLETE_COLLECTION_FAILURE,
  DUPLICATE_COLLECTION_REQUEST,
  DUPLICATE_COLLECTION_SUCCESS,
  DUPLICATE_COLLECTION_FAILURE,
  CLEAR_STATUS_CHANGE_NOTIFICATION,
  SET_ACTIVATION_VALIDATION_ERROR,
  CLEAR_ACTIVATION_VALIDATION_ERROR,
  CLEAR_DUPLICATED_COLLECTION,
  COLLECTION_FEATURES_REQUEST,
  COLLECTION_FEATURES_RECEIVED,
  COLLECTION_FEATURES_FAILED,
} from 'actions/collection'

export const initialState = Map({
  collectionNotification: null,
  collection: null,
  createdCollection: null,
  isLoading: null,
  fetchError: false,
  requestedCollectionId: null, // ID of the most recently requested collection (gates late responses)
  collections: null,
  isStatusUpdating: false, // PBI 150666: Lifecycle status change loading state
  statusUpdatingCollectionId: null, // ID of the collection being updated (scopes loading state)
  isDuplicating: false, // PBI 150666: Duplicate collection loading state
  duplicatedCollection: null, // PBI 150666: Newly duplicated collection data
  statusChangeNotification: null, // PBI 150666: Success notification after status change
  activationValidationError: null, // PBI 150666: Validation error when trying to activate with obsolete features
  collectionFeatures: null, // Features at pinned revisions (bypasses search reducer normalization)
  isCollectionFeaturesLoading: false,
  collectionFeaturesError: false, // True when the features fetch fails — drives inline error UI
  requestedFeaturesCollectionId: null, // Gates late feature responses after navigation
})

const handlers = {
  [CREATE_COLLECTION_FAILURE]: collectionNotification,
  [CREATE_COLLECTION_SUCCESS]: collectionNotification,
  [CLEAR_CREATE_COLLECTION_NOTIFICATION]: clearCreateNotificationMessage,
  [CLEAR_CREATED_COLLECTION]: clearCreatedCollectionData,
  [GET_COLLECTIONS_SUCCESS]: getCollectionsSuccess,
  [GET_COLLECTIONS_REQUEST]: getCollectionsRequest,
  [GET_COLLECTIONS_FAILURE]: getCollectionsFailure,
  [GET_COLLECTION_SUCCESS]: getCollectionSuccess,
  [GET_COLLECTION_REQUEST]: getCollectionRequset,
  [GET_COLLECTION_FAILURE]: getCollectionFailure,
  [RESET_COLLECTIONS]: resetCollections,
  [RESET_COLLECTION]: resetCollection,
  [UPDATE_COLLECTION_REQUEST]: updateCollectionRequest,
  [UPDATE_COLLECTION_SUCCESS]: updateCollectionSuccess,
  [UPDATE_COLLECTION_FAILURE]: updateCollectionFailure,
  // Collection Lifecycle Handlers (PBI 150666)
  [ACTIVATE_COLLECTION_REQUEST]: statusUpdateRequest,
  [ACTIVATE_COLLECTION_SUCCESS]: statusUpdateSuccess,
  [ACTIVATE_COLLECTION_FAILURE]: statusUpdateFailure,
  [OBSOLETE_COLLECTION_REQUEST]: statusUpdateRequest,
  [OBSOLETE_COLLECTION_SUCCESS]: statusUpdateSuccess,
  [OBSOLETE_COLLECTION_FAILURE]: statusUpdateFailure,
  [DUPLICATE_COLLECTION_REQUEST]: duplicateRequest,
  [DUPLICATE_COLLECTION_SUCCESS]: duplicateSuccess,
  [DUPLICATE_COLLECTION_FAILURE]: duplicateFailure,
  [CLEAR_DUPLICATED_COLLECTION]: clearDuplicatedCollectionState,
  [CLEAR_STATUS_CHANGE_NOTIFICATION]: clearStatusNotification,
  [SET_ACTIVATION_VALIDATION_ERROR]: setValidationError,
  [CLEAR_ACTIVATION_VALIDATION_ERROR]: clearValidationError,
  // Collection features by ID (revision-pinned, bypasses search normalization)
  [COLLECTION_FEATURES_REQUEST]: collectionFeaturesRequest,
  [COLLECTION_FEATURES_RECEIVED]: collectionFeaturesReceived,
  [COLLECTION_FEATURES_FAILED]: collectionFeaturesFailure,
}

export default createReducer(initialState, handlers)

function resetCollections() {
  return initialState
}

function resetCollection(state) {
  return (
    state
      .set('collection', null)
      .set('fetchError', false)
      .set('isLoading', false)
      .set('requestedCollectionId', null)
      .set('collectionFeatures', null)
      .set('isCollectionFeaturesLoading', false)
      .set('collectionFeaturesError', false)
      .set('requestedFeaturesCollectionId', null)
      // Bug 505198: Clear lifecycle in-flight state on navigation so stale
      // operations from the previous collection don't block the new one.
      .set('isStatusUpdating', false)
      .set('statusUpdatingCollectionId', null)
      // Bug 505198: Clear activation validation error and status notification on
      // navigation. A stale activationValidationError (e.g. hasObsoleteFeatures:
      // true) from a prior collection would cause handleActivateClick to silently
      // block activation on the freshly-created/navigated-to collection.
      .set('activationValidationError', null)
      .set('statusChangeNotification', null)
  )
}

function getCollectionRequset(state, { id }) {
  return state
    .set('isLoading', true)
    .set('fetchError', false)
    .set('requestedCollectionId', id || null)
}

function getCollectionFailure(state, { id: requestId }) {
  // Gate: ignore failures from stale requests (same logic as success)
  const expectedId = state.get('requestedCollectionId')
  if (expectedId && requestId && String(requestId) !== String(expectedId)) {
    return state
  }
  return state.set('isLoading', false).set('fetchError', true)
}

function getCollectionSuccess(state, { data, id: requestId }) {
  // Gate: only store the response if it belongs to the most recently requested
  // collection. Prevents a late re-fetch (e.g. lifecycle-triggered) for
  // collection A from overwriting collection B after navigation.
  const expectedId = state.get('requestedCollectionId')
  if (expectedId && requestId && String(requestId) !== String(expectedId)) {
    return state
  }
  return state
    .set('collection', fromJS(data))
    .set('isLoading', false)
    .set('fetchError', false)
}

function getCollectionsRequest(state) {
  return state.set('isLoading', true)
}

function getCollectionsFailure(state) {
  return state.set('isLoading', false)
}

function getCollectionsSuccess(state, { data }) {
  const { groups } = data

  const immuGroups = Map(toMap('id', groups))

  return state.set('collections', immuGroups).set('isLoading', false)
}

function updateCollectionRequest(state) {
  return state.set('isLoading', true)
}

function updateCollectionSuccess(state, { data }) {
  return state.set('collection', fromJS(data)).set('isLoading', false)
}

function updateCollectionFailure(state) {
  return state.set('isLoading', false)
}

function collectionNotification(state, action) {
  const { title, type, errorMessage, data } = action

  const getNofiticationType = (t) => {
    if (t === CREATE_COLLECTION_SUCCESS) return 'success'

    if (t === CREATE_COLLECTION_FAILURE) return 'error'

    return false
  }

  const notificationType = getNofiticationType(type)

  if (!notificationType) return state

  let newState = state.set(
    'collectionNotification',
    fromJS({
      title,
      type: notificationType,
      errorMessage,
    })
  )

  // Store the created collection data for post-creation navigation
  if (type === CREATE_COLLECTION_SUCCESS && data) {
    newState = newState.set('createdCollection', fromJS(data))
  }

  return newState
}

function clearCreateNotificationMessage(state) {
  return state.set('collectionNotification', false)
}

function clearCreatedCollectionData(state) {
  return state.set('createdCollection', null)
}

// Collection Lifecycle Handler Functions (PBI 150666)
function statusUpdateRequest(state, { id }) {
  return state
    .set('isStatusUpdating', true)
    .set('statusUpdatingCollectionId', id || null)
    .set('statusChangeNotification', null)
}

// Whitelist of known collection fields to prevent merging saga/API metadata
const COLLECTION_FIELDS = [
  'id',
  'name',
  'description',
  'status',
  'type',
  'vehicleSubType',
  'varMinCompat',
  'mdlDbCd',
  'partUris',
  'parts',
  'createdBy',
  'createdDate',
  'modifiedBy',
  'modifiedDate',
  'buildDate',
]

function statusUpdateSuccess(state, action) {
  // Bug 505198: The apiCall saga spreads response data into the action when
  // the API returns { result, data } format. In that case action.data is
  // undefined and the group fields are directly on the action. Extract them.
  //
  // action.id is ALWAYS the request's collection ID (from ...args spread in
  // apiCall saga). Use it as the authoritative ID for gating and notification,
  // regardless of whether the response body includes an id field.
  const { type, id: requestId, data, ...rest } = action
  const rawData = data || rest

  // Only pick known collection fields to avoid merging saga metadata
  const responseData = Object.keys(rawData).reduce((acc, key) => {
    if (COLLECTION_FIELDS.includes(key)) acc[key] = rawData[key]
    return acc
  }, {})

  // Gate merge by matching IDs: only merge if the request's collection ID
  // matches the collection currently in state. Prevents late responses from
  // a previous collection overwriting data after the user navigated away.
  // When no collection is loaded (null after resetCollection during navigation),
  // do NOT create a new entry from the lifecycle response — the route-matched
  // refetch is the authoritative source for collection data.
  const currentCollection = state.get('collection')
  const currentId = currentCollection ? currentCollection.get('id') : null
  const idsMatch =
    currentCollection && (!requestId || String(requestId) === String(currentId))

  const updatedCollection =
    idsMatch && Object.keys(responseData).length > 0
      ? currentCollection.merge(fromJS(responseData))
      : currentCollection

  // Determine notification type based on action
  const isActivation = type === 'ACTIVATE_COLLECTION_SUCCESS'
  const notificationType = isActivation ? 'activated' : 'obsoleted'

  // Only clear loading state if this response belongs to the in-flight operation.
  // A late response from collection A should not clear the loading flag if the
  // user has already started a new operation on collection B.
  const inFlightId = state.get('statusUpdatingCollectionId')
  const isCurrentOperation =
    !requestId || !inFlightId || String(requestId) === String(inFlightId)

  let newState = state
    .set('collection', updatedCollection)
    .set(
      'isStatusUpdating',
      isCurrentOperation ? false : state.get('isStatusUpdating')
    )
    .set('statusUpdatingCollectionId', isCurrentOperation ? null : inFlightId)

  // Only publish the notification when the response belongs to the active
  // lifecycle operation. Stale notifications from collection A should not
  // linger in state while collection B is displayed.
  if (isCurrentOperation) {
    newState = newState.set(
      'statusChangeNotification',
      fromJS({
        type: notificationType,
        collectionId: requestId || currentId,
        timestamp: Date.now(),
      })
    )
  }

  return newState
}

function statusUpdateFailure(state, { type, id: requestId }) {
  // Determine notification type based on action
  const isActivation = type === 'ACTIVATE_COLLECTION_FAILURE'
  const notificationType = isActivation ? 'activate_error' : 'obsolete_error'

  // Only clear loading state if this failure belongs to the in-flight operation
  const inFlightId = state.get('statusUpdatingCollectionId')
  const isCurrentOperation =
    !requestId || !inFlightId || String(requestId) === String(inFlightId)

  let newState = state
    .set(
      'isStatusUpdating',
      isCurrentOperation ? false : state.get('isStatusUpdating')
    )
    .set('statusUpdatingCollectionId', isCurrentOperation ? null : inFlightId)

  if (isCurrentOperation) {
    newState = newState.set(
      'statusChangeNotification',
      fromJS({
        type: notificationType,
        collectionId: requestId || null,
        timestamp: Date.now(),
      })
    )
  }

  return newState
}

// Duplicate Collection Handler Functions (PBI 150666)
function duplicateRequest(state) {
  return state.set('isDuplicating', true).set('duplicatedCollection', null)
}

function duplicateSuccess(state, { data }) {
  return state
    .set('isDuplicating', false)
    .set('duplicatedCollection', fromJS(data))
}

function duplicateFailure(state) {
  return state.set('isDuplicating', false).set('duplicatedCollection', null)
}

// Bug 479018: Clear duplicated collection state after navigation
function clearDuplicatedCollectionState(state) {
  return state.set('duplicatedCollection', null)
}

// PBI 150666: Clear status change notification
function clearStatusNotification(state) {
  return state.set('statusChangeNotification', null)
}

// PBI 150666: Activation validation error handlers
function setValidationError(state, { error }) {
  return state.set('activationValidationError', fromJS(error))
}

function clearValidationError(state) {
  return state.set('activationValidationError', null)
}

// Collection features by ID (revision-pinned)
function collectionFeaturesRequest(state, { collectionId }) {
  return state
    .set('isCollectionFeaturesLoading', true)
    .set('collectionFeatures', null)
    .set('collectionFeaturesError', false)
    .set('requestedFeaturesCollectionId', collectionId || null)
}

function collectionFeaturesReceived(state, action) {
  // Gate: discard stale feature responses from a previous collection
  const expectedId = state.get('requestedFeaturesCollectionId')
  const responseId = action.collectionId
  if (expectedId && responseId && String(responseId) !== String(expectedId)) {
    return state
  }

  // The API response shape is { features: [...] }. Filter out entries with
  // null ids/uris (same filtering that was previously done in the component).
  const features = action.data?.features || []
  // apiCall.js spreads args to the top level of the success action, so the ids
  // array is at action.ids — NOT action.args?.ids (action.args is never set by
  // the saga). The short-circuit path in fetchCollectionFeatures (empty ids)
  // dispatches a plain object directly, which also reaches here but has no ids
  // key, so the || [] fallback correctly produces an empty filtered result.
  const ids = (action.ids || []).map(String)
  const filtered = features.filter((f) => ids.includes(String(f.id)))
  return (
    state
      .set('isCollectionFeaturesLoading', false)
      // Explicitly clear the error flag so that a short-circuit dispatch of
      // COLLECTION_FEATURES_RECEIVED (ids=[]) after an error state still resets
      // the UI — the short-circuit bypasses COLLECTION_FEATURES_REQUEST which is
      // the normal path that clears this flag.
      .set('collectionFeaturesError', false)
      .set('collectionFeatures', fromJS(filtered))
  )
}

function collectionFeaturesFailure(state, action) {
  // Gate: discard stale feature failures from a previous collection
  const expectedId = state.get('requestedFeaturesCollectionId')
  const responseId = action.collectionId
  if (expectedId && responseId && String(responseId) !== String(expectedId)) {
    return state
  }
  return state
    .set('isCollectionFeaturesLoading', false)
    .set('collectionFeatures', null)
    .set('collectionFeaturesError', true)
}
